import { subDays, subMilliseconds, subYears } from 'date-fns'
import {
  ExameEspecificoEnum,
  MedicoesAtendimentosAnterioresQuery,
  ResultadoExameInput,
  TipoAtendimentoProfissional,
  TipoGravidezEnum,
} from 'graphql/types.generated'
import { SdcAtendimentoValues } from 'hooks/sdc/model-sdc'
import { calcularIMC } from 'util/atendimento/calculateImc'
import { isBetween } from 'util/date/compare'
import { toDate } from 'util/date/formatDate'
import { SoapState } from 'view/atendimentos/atendimento-individual/model'
import {
  convertMedicaoAnterior,
  convertMedicaoOutrosAtendimentos,
  convertMedicoesNoNascimento,
} from 'view/atendimentos/detail/components/modals/medicoes/converter-medicoes'
import { convertResultadosExamesToInput } from 'view/atendimentos/detail/soap/objetivo/converter-objetivo'
import { FormAtivoObjetivoEnum } from 'view/atendimentos/detail/soap/objetivo/model'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

const TIPOS_GRAVIDEZ_MULTIPLAS = [TipoGravidezEnum.DUPLA_GEMELAR, TipoGravidezEnum.TRIPLA_OU_MAIS]

export const buildSdcValues = (
  soap: SoapState | undefined,
  prontuarioId: ID,
  iniciadoEm: number,
  isRegistroTardio: boolean,
  cidadaoDataNascimento: string,
  dataMedicoes: MedicoesAtendimentosAnterioresQuery | undefined,
  hoje: Date,
  tipoAtendimentoProfissional: TipoAtendimentoProfissional | null
): SdcAtendimentoValues => {
  const problemasCondicoesLpc = soap?.problemasECondicoes || []
  const problemasCondicoesAvaliacao = soap?.avaliacao?.problemasECondicoes || []
  const problemasCondicoes = [...problemasCondicoesLpc, ...problemasCondicoesAvaliacao]

  const formAtivoObjetivo = soap?.objetivo?.atendimentosEspecificos?.formAtivo

  const hojeNoInicioDoDia = new Date(hoje)
  hojeNoInicioDoDia.setUTCHours(0, 0, 0, 0)

  const diaAnterior = subMilliseconds(subDays(hojeNoInicioDoDia, 1), 1)

  const medicoesOutrosAtendimentos =
    dataMedicoes != undefined ? buildMedicoesOutrosAtendimentos(dataMedicoes, cidadaoDataNascimento) : []

  const { peso: pesoObjetivo, altura: alturaObjetivo } = soap?.objetivo?.antropometria ?? {}
  const { pressaoArterialSistolica } = soap?.objetivo?.sinaisVitais ?? {}

  const peso = getPesoUltimas24Horas(pesoObjetivo, soap, medicoesOutrosAtendimentos, diaAnterior, hojeNoInicioDoDia)

  const imcAtendimentoObjetivo = pesoObjetivo && alturaObjetivo ? calcularIMC(pesoObjetivo, alturaObjetivo) : null

  const imcMedicoesAnteriores =
    soap != null ? getImcMedicoesAnteriores(soap, medicoesOutrosAtendimentos, diaAnterior, hojeNoInicioDoDia) : null

  const imcAtendimentoAtual = imcAtendimentoObjetivo ?? imcMedicoesAnteriores

  const resultadosExames =
    soap?.objetivo?.resultadosExames != null ? convertResultadosExamesToInput(soap.objetivo.resultadosExames) : null

  const colesterolTotal = resultadosExames ? getColesterolTotalRecente(resultadosExames, hojeNoInicioDoDia) : null

  return {
    problemasCondicoes,
    prontuarioId,
    atendProfData: toDate(iniciadoEm),
    pesoUltimas24Horas: peso,
    isRegistroTardio,
    isGravidezMultipla:
      soap?.preNatal?.tipoGravidez?.value != undefined &&
      TIPOS_GRAVIDEZ_MULTIPLAS.includes(soap.preNatal.tipoGravidez.value),
    isAtendimentoPreNatal: formAtivoObjetivo === FormAtivoObjetivoEnum.PRE_NATAL,
    pressaoArterialSistolica: pressaoArterialSistolica ?? null,
    colesterolTotalRecente: colesterolTotal,
    imcUltimas24Horas: imcAtendimentoAtual,
    tipoAtendimentoProfissional,
  }
}

const buildMedicoesOutrosAtendimentos = (
  dataMedicoes: MedicoesAtendimentosAnterioresQuery,
  cidadaoDataNascimento: string
): HistoricoMedicaoModel[] => {
  const medicoesAtendimentosAnteriores = dataMedicoes?.medicoes ?? []

  const medicoesAtendimentosAnterioresOrdenadasMaisRecentePrimeiro = medicoesAtendimentosAnteriores.toSorted(
    (a, b) => b.dataMedicao - a.dataMedicao
  )

  const historicoMedicoesAtendimentosAnteriores = medicoesAtendimentosAnterioresOrdenadasMaisRecentePrimeiro.map(
    convertMedicaoOutrosAtendimentos
  )

  const medicoesNoNascimento = dataMedicoes?.medicoesNoNascimento ?? null

  const historicoMedicoesNoNascimento = medicoesNoNascimento
    ? [convertMedicoesNoNascimento(medicoesNoNascimento, cidadaoDataNascimento)]
    : []

  return [...historicoMedicoesAtendimentosAnteriores, ...historicoMedicoesNoNascimento]
}

const getPesoUltimas24Horas = (
  pesoObjetivo: string | undefined,
  soap: SoapState | undefined,
  medicoesOutrosAtendimentos: HistoricoMedicaoModel[],
  diaAnterior: Date,
  hoje: Date
): string | null => {
  const pesoInformadoNoObjetivo = pesoObjetivo ?? null
  if (pesoInformadoNoObjetivo != null) return pesoInformadoNoObjetivo

  const existeSoap = soap != null && soap != undefined
  if (!existeSoap) return null

  const pesoMaisRecenteNasMedicoesAnteriores = getPesoMedicoesAnteriores(
    soap,
    medicoesOutrosAtendimentos,
    diaAnterior,
    hoje
  )

  return pesoMaisRecenteNasMedicoesAnteriores
}

const getPesoMedicoesAnteriores = (
  soap: SoapState,
  medicoesOutrosAtendimentos: HistoricoMedicaoModel[],
  startRange: Date,
  endRange: Date
): string | null => {
  const medicoesAnterioresDoSoap = soap?.medicoesAnteriores?.map(convertMedicaoAnterior) ?? []
  const medicoesConsideradasParaPeso = [...medicoesAnterioresDoSoap, ...medicoesOutrosAtendimentos]

  const medicoesComPesoDentroDoPeriodo = medicoesConsideradasParaPeso.filter((medicao) => {
    const dataDaMedicaoComoDate = toDate(medicao.dataMedicao)
    const possuiValorDePeso = medicao.valorPeso != null && medicao.valorPeso !== undefined
    const estaDentroDoIntervalo = isBetween(dataDaMedicaoComoDate, startRange, endRange)

    return possuiValorDePeso && estaDentroDoIntervalo
  })

  const medicoesComPesoOrdenadasMaisRecentePrimeiro = medicoesComPesoDentroDoPeriodo.toSorted((a, b) =>
    b.dataMedicao > a.dataMedicao ? 1 : -1
  )

  const medicaoMaisRecenteComPeso = medicoesComPesoOrdenadasMaisRecentePrimeiro?.[0] ?? null
  const pesoMaisRecente = medicaoMaisRecenteComPeso?.valorPeso?.toString() ?? null

  return pesoMaisRecente
}

const isImcMedicaoDentroDaJanela = (medicao: HistoricoMedicaoModel, startRange: Date, endRange: Date) => {
  const dataDaMedicaoComoDate = toDate(medicao.dataMedicao)
  const possuiValorDeImc = medicao.valorImc != null
  const estaDentroDoIntervalo = isBetween(dataDaMedicaoComoDate, startRange, endRange)

  return possuiValorDeImc && estaDentroDoIntervalo
}

const getImcMedicoesAnteriores = (
  soap: SoapState,
  medicoesOutrosAtendimentos: HistoricoMedicaoModel[],
  startRange: Date,
  endRange: Date
): number | null => {
  const medicoesAnterioresDoSoap = soap?.medicoesAnteriores?.map(convertMedicaoAnterior) ?? []
  const medicoesConsideradasParaImc = [...medicoesAnterioresDoSoap, ...medicoesOutrosAtendimentos]

  const medicoesComImcDentroDoPeriodo = medicoesConsideradasParaImc.filter((medicao) =>
    isImcMedicaoDentroDaJanela(medicao, startRange, endRange)
  )

  const medicoesComImcOrdenadasMaisRecentePrimeiro = medicoesComImcDentroDoPeriodo.toSorted((a, b) =>
    b.dataMedicao > a.dataMedicao ? 1 : -1
  )

  const medicaoMaisRecenteComImc = medicoesComImcOrdenadasMaisRecentePrimeiro?.[0] ?? null
  const imcMaisRecente = medicaoMaisRecenteComImc?.valorImc ?? null

  return imcMaisRecente
}

const getColesterolTotalRecente = (resultadosExames: ResultadoExameInput[], hoje: Date) => {
  const limiteInferior = subYears(hoje, 1).getTime()

  const resultadosComColesterolTotalValido = resultadosExames.filter((resultadoExame: ResultadoExameInput) => {
    const procedimentoEhColesterolTotal =
      resultadoExame.especifico?.procedimento === ExameEspecificoEnum.COLESTEROL_TOTAL
    const possuiValor = resultadoExame.especifico?.valor != null
    if (!procedimentoEhColesterolTotal || !possuiValor) return false

    const dataRef = resultadoExame.dataRealizacao
    if (!dataRef) return false

    return new Date(dataRef).getTime() >= limiteInferior
  })

  const resultadosOrdenadosMaisRecentePrimeiro = resultadosComColesterolTotalValido.toSorted((a, b) => {
    const dataMaisRecenteB = new Date(b.dataResultado ?? b.dataRealizacao).getTime()
    const dataMaisRecenteA = new Date(a.dataResultado ?? a.dataRealizacao).getTime()
    return dataMaisRecenteB - dataMaisRecenteA
  })

  return resultadosOrdenadosMaisRecentePrimeiro?.[0] ?? null
}
